/**
 * Store utility functions for parsing and validating app identifiers and URLs.
 */

export const StoreIdentifier = {
  APPSTORE: "appstore",
  PLAYSTORE: "playstore",
} as const;

export type StoreIdentifierType = (typeof StoreIdentifier)[keyof typeof StoreIdentifier];

/**
 * Validates a Google Play bundle ID format.
 * Bundle IDs follow the pattern: com.company.appname
 */
export const bundleIdCheck = (id: string): boolean =>
  /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)+$/.test(id);

/**
 * Validates an App Store numeric app ID.
 * App IDs are 9-13 digit numbers, optionally prefixed with "id".
 */
export const appIdCheck = (id: string): boolean => /^(?:id)?\d{9,13}$/.test(id);

/**
 * Extract App Store app ID from URL.
 * Matches all valid App Store URL formats:
 * - https://apps.apple.com/us/app/app-name/id123456789 (with region and app name)
 * - https://apps.apple.com/us/app/id123456789 (with region, no app name)
 * - https://apps.apple.com/app/id123456789 (no region, no app name)
 * - https://apps.apple.com/app/app-name/id123456789 (no region, with app name)
 */
export function getAppleAppIdFromUrl(str: string): string | null {
  const match = str.match(/^https:\/\/apps\.apple\.com\/(?:[^/]+\/)?app\/(?:[^/]+\/)?(id\d{9,})/);
  return match ? match[1].replace(/^id/, "") : null;
}

/**
 * Extract region from App Store URL.
 * - https://apps.apple.com/us/app/id6751536221 -> "us"
 * - https://apps.apple.com/app/id6751536221 -> null (first segment is "app")
 */
export function getAppleRegionFromUrl(str: string): string | null {
  try {
    const url = new URL(str);
    if (!url.hostname.includes("apps.apple.com")) return null;
    const pathSegments = url.pathname.split("/").filter(Boolean);
    if (pathSegments.length > 0 && pathSegments[0] !== "app") {
      return pathSegments[0];
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Extract bundle ID from Play Store URL.
 * - https://play.google.com/store/apps/details?id=com.example.app -> "com.example.app"
 */
export function getPlayStoreBundleIdFromUrl(str: string): string | null {
  const match = str.match(
    /^https:\/\/play\.google\.com\/store\/apps\/details\?id=([^&]+)/
  );
  return match ? match[1] : null;
}

/**
 * Extract language from Play Store URL using the 'hl' query parameter.
 * - https://play.google.com/store/apps/details?id=com.example&hl=en -> "en"
 * - https://play.google.com/store/apps/details?id=com.example -> null
 */
export function getPlayStoreRegionFromUrl(str: string): string | null {
  try {
    const url = new URL(str);
    if (!url.hostname.includes("play.google.com")) return null;
    const hl = url.searchParams.get("hl");
    return hl && hl.length > 0 ? hl : null;
  } catch {
    return null;
  }
}

/**
 * Extract country from Play Store URL using the 'gl' query parameter.
 * - https://play.google.com/store/apps/details?id=com.example&gl=us -> "us"
 * - https://play.google.com/store/apps/details?id=com.example -> null
 */
export function getPlayStoreCountryFromUrl(str: string): string | null {
  try {
    const url = new URL(str);
    if (!url.hostname.includes("play.google.com")) return null;
    const gl = url.searchParams.get("gl");
    return gl && gl.length > 0 ? gl : null;
  } catch {
    return null;
  }
}

/**
 * Build a canonical store-listing URL from a raw app identifier. For the App
 * Store the numeric id is normalized so the result ALWAYS carries exactly one
 * `id` prefix (round-trips through getAppleAppIdFromUrl). Accepts bare
 * ("324684580") or already-prefixed ("id324684580") ids. Play bundle ids are
 * passed through `encodeURIComponent` as defensive hardening — a no-op for
 * valid bundle ids ([a-zA-Z0-9_.], all unreserved characters), so existing
 * call sites stay byte-identical while malformed ids can no longer break the
 * query string.
 */
export function getStoreUrlFromIdentifier(storeId: StoreIdentifierType, appId: string): string {
  if (storeId === StoreIdentifier.APPSTORE) {
    const numericId = appId.replace(/^id/i, "");
    return `https://apps.apple.com/app/id${numericId}`;
  }
  return `https://play.google.com/store/apps/details?id=${encodeURIComponent(appId)}`;
}

/**
 * Region/country-aware variant. App Store: region in path (defaults "us").
 * Play Store: appends &gl=<country> when provided. Same Apple id normalization.
 * Play bundle ids are `encodeURIComponent`-wrapped (a no-op for valid ids).
 */
export function getStoreUrlFromIdentifierWithRegion(storeId: StoreIdentifierType, appId: string, countryCode?: string): string {
  if (storeId === StoreIdentifier.APPSTORE) {
    const region = countryCode || "us";
    const numericId = appId.replace(/^id/i, "");
    return `https://apps.apple.com/${region}/app/id${numericId}`;
  }
  const base = `https://play.google.com/store/apps/details?id=${encodeURIComponent(appId)}`;
  return countryCode ? `${base}&gl=${countryCode}` : base;
}

/**
 * Extract store ID (app ID or bundle ID) from a store URL.
 * Returns the original value if not a recognized store URL.
 */
export function storeIdUrlCheck(value: string): string {
  const isUrl = value.startsWith("https://");
  if (!isUrl) {
    return value;
  }
  // App Store URL
  const isAppleUrl = value.startsWith("https://apps.apple.com/");
  if (isAppleUrl) {
    const appleAppId = getAppleAppIdFromUrl(value);
    if (appleAppId) {
      return appleAppId;
    }
  }

  // Play Store URL
  const isPlayStoreUrl = value.startsWith("https://play.google.com/store/apps");
  if (isPlayStoreUrl) {
    const playStoreBundleId = getPlayStoreBundleIdFromUrl(value);
    if (playStoreBundleId) {
      return playStoreBundleId;
    }
  }

  return value;
}
